import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import type { AssetLOTO } from "@/pages/asset-details/models/AssetLOTO";
import { updateAssetLOTOComments } from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { ErrorModal, useToken } from "@dexteel/mesf-core";
import {
	Button,
	Card,
	CardContent,
	CircularProgress,
	createStyles,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	Grid,
	makeStyles,
	Snackbar,
	TextField,
	Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import moment from "moment";
import { type SyntheticEvent, useEffect, useState } from "react";

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			"& .input-group-text": {
				display: "none",
			},
			"& .form-control": {
				height: "42vh !important",
			},
		},
		modalTitle: {
			backgroundColor: "#ced2cc",
			color: "#495057",
			textAlign: "center",
		},
		modalWarning: {
			color: "white",
			backgroundColor: "#dc3545",
			textAlign: "center",
		},
		messageTitle: {
			fontSize: 15,
			fontWeight: "bold",
			color: "#0000008A",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	rowSelected: AssetLOTO;
	searchAssetLOTO: Function;
};

export const CommentsDialog = ({
	show,
	onHide,
	rowSelected,
	searchAssetLOTO,
}: Props) => {
	const classes = useStyles();
	const { getUserName } = useToken();
	const [editCommentSuccess, setEditCommentSuccess] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");

	const {
		state: { assetLOTOComments },
		actions: { setAssetLOTOComments },
	} = useAssetDetailsContext();
	//states
	const [commentInput, setCommentInput] = useState<string>("");

	//functions

	const appendComment = (newComment: string) => {
		const now = moment().format("DD/MM/YYYY HH:mm");
		let newValue = "";

		if (
			assetLOTOComments === "" ||
			assetLOTOComments === null ||
			assetLOTOComments === "null"
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				assetLOTOComments +
				"\n" +
				getUserName() +
				" - " +
				now +
				" - " +
				newComment;
		}

		if (newComment != "") {
			setCommentInput("");
			setAssetLOTOComments(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(commentInput);
	};

	const onKeyDown = (e: any) => {
		if (e.key === "Enter") {
			appendComment(e.target.value);
			e.preventDefault();
		}
	};

	const handleClose = (event?: SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setEditCommentSuccess(false);
	};

	const handleCancel = () => {
		onHide(false);
		setCommentInput("");
	};

	const handleSaveBtn = async () => {
		setIsSubmitLoading(true);
		const res = await updateAssetLOTOComments(
			rowSelected.AssetId,
			rowSelected.LOTONumber as string,
			assetLOTOComments,
		);
		if (res.ok) {
			searchAssetLOTO();
			setIsSubmitLoading(false);
			setEditCommentSuccess(true);
			onHide(true);
		} else {
			setError(res.message);
			setIsSubmitLoading(false);
		}
	};

	const changeComments = (value: string) => {
		setAssetLOTOComments(value);
	};

	useEffect(() => {
		if (rowSelected != null && show) {
			setAssetLOTOComments(rowSelected.Comments);
			setCommentInput("");
			setIsSubmitLoading(false);
		}
	}, [show]);

	return (
		<>
			<Dialog
				fullScreen={true}
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				style={{ margin: "20px 40px" }}
				open={show}
				onClose={() => handleCancel()}
			>
				<Grid container style={{ height: "100%", padding: "15px 0 0" }}>
					<Grid item md={12} xs={12}>
						<DialogTitle
							id="max-width-dialog-title"
							style={{ padding: "10px 24px 10px" }}
						>
							TITLE: {rowSelected?.LOTOName}
						</DialogTitle>
						<div style={{ padding: "0px 24px " }}>
							<p id="max-width-dialog-title" style={{ marginBottom: "0px" }}>
								{" "}
								ASSET: {rowSelected?.AssetName}
							</p>
						</div>
					</Grid>
					<Grid container item>
						<DialogContent dividers>
							<Grid container style={{ height: "61vh" }}>
								<Grid
									item
									md={12}
									xs={12}
									style={{ height: "85%", marginTop: "15px" }}
								>
									<Card
										variant="outlined"
										style={{ height: "100%", backgroundColor: "#E9F2F8" }}
										className={classes.root}
									>
										<CardContent>
											<Typography className={classes.messageTitle}>
												Comments:
											</Typography>
											<SimpleTextAreaControl
												value={assetLOTOComments}
												onChange={changeComments}
												disabled
												showColor={false}
												styleText={{
													border: "none",
													backgroundColor: "#E9F2F8",
													fontSize: 16,
												}}
											/>
										</CardContent>
									</Card>
								</Grid>
								<Grid
									container
									spacing={2}
									justifyContent="center"
									alignItems="center"
								>
									<Grid item md={9} xs={12}>
										<TextField
											variant="outlined"
											margin="dense"
											multiline
											maxRows={3}
											value={commentInput}
											onChange={(e) => setCommentInput(e.target.value)}
											label="Comment"
											fullWidth
											onKeyDown={onKeyDown}
											autoComplete="off"
										/>
									</Grid>
									<Grid item md={3} xs={12}>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											onClick={addNewComment}
											style={{ height: "40px", marginTop: 2 }}
										>
											Add comment
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</DialogContent>
					</Grid>

					<Grid
						item
						md={12}
						xs={12}
						style={{ padding: "10px 0 15px", margin: "auto 0" }}
					>
						<DialogActions style={{ padding: "0px 24px" }}>
							<Grid item md={3} xs={6} style={{ marginRight: "20px" }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={handleCancel}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={6} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									onClick={handleSaveBtn}
								>
									Save
								</Button>
							</Grid>
						</DialogActions>
					</Grid>
				</Grid>
			</Dialog>
			<Snackbar
				open={editCommentSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="info" onClose={handleClose}>
					Comment edited
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
